// useAutoSaveChat — silently persists the active conversation to
// `chat_conversations` so it shows up in the user's "Recent chats"
// list even if they never explicitly hit Save or Share.
//
// Why this exists: previously a chat only became a row when the user
// clicked Save-as-playbook or Share. That meant the Recent tab was
// almost always empty for casual users — they couldn't "identify runs
// quickly" because the runs simply weren't in the list. Auto-save
// fixes that without changing the explicit Save flow (which still
// adds skill credits, cover images, summaries, etc.).
//
// Behaviour:
//   - Waits until there's at least one user message AND one
//     assistant message before creating a row. A lone "hi" gets
//     dropped — the list stays clean.
//   - First insert mints the row id and stores it in a ref so the
//     parent shell can reuse it (e.g. for share). Caller passes a
//     setter to expose the id outside the hook if they want it.
//   - Subsequent updates are debounced 1.5s so we don't write on
//     every streamed chunk.
//   - Title is derived once on insert and re-derived only if the
//     title is still the auto-generated one (so a user who renamed
//     the chat in the Save dialog doesn't get their custom name
//     overwritten by us).
import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { deriveChatTitle } from "@/lib/deriveChatTitle";
import type { ChatType } from "@/lib/chatType";

export interface AutoSaveTurn {
  role: "user" | "assistant" | "system";
  content: string;
  streaming?: boolean;
}

interface Options {
  turns: AutoSaveTurn[];
  model: string;
  /** Pulled from the chat seed bus, if any; drives the smart prefix. */
  seedSource?: string | null;
  seedTitle?: string | null;
  /** Explicit type chosen in the composer's type picker. Wins over
   *  seed-derived prefix so "Task" / "Quest" / "Challenge" actually
   *  show up in the Recent list grouping. */
  chatType?: ChatType;
  /** Caller-owned id state. Pass current id; we'll set it on first save. */
  conversationId: string | null;
  setConversationId: (id: string) => void;
  /** Soft kill switch — caller can disable (e.g. for shared/read-only views). */
  enabled?: boolean;
}

const DEBOUNCE_MS = 1500;

export function useAutoSaveChat({
  turns,
  model,
  seedSource,
  seedTitle,
  chatType,
  conversationId,
  setConversationId,
  enabled = true,
}: Options) {
  const { user } = useAuth();

  // Track the title we generated so we can detect whether the row's
  // current title is still ours (safe to refresh) or has been edited
  // by the user in SaveAsPlaybook (we should leave it alone).
  const ourLastTitleRef = useRef<string | null>(null);
  const inFlightRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!enabled || !user) return;
    // Need at least one *completed* assistant turn to make a row worth
    // saving. Streaming turns aren't done yet — wait for them.
    const hasUser = turns.some((t) => t.role === "user" && t.content.trim());
    const hasCompletedAssistant = turns.some(
      (t) => t.role === "assistant" && t.content.trim() && !t.streaming,
    );
    if (!hasUser || !hasCompletedAssistant) return;

    // Snapshot a slim payload — strip transient flags like `streaming`
    // so the persisted row doesn't carry runtime state.
    const payload = turns
      .filter((t) => (t.role === "user" || t.role === "assistant") && t.content.trim())
      .map((t) => ({ role: t.role, content: t.content }));

    const firstUserLine =
      turns.find((t) => t.role === "user")?.content?.split("\n")[0] ?? null;

    const title = deriveChatTitle({
      seedSource,
      seedTitle,
      firstUserLine,
      chatType,
    });

    // Debounce: collapse rapid streaming-completion bursts into one write.
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      void run();
    }, DEBOUNCE_MS);

    async function run() {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      try {
        if (!conversationId) {
          const { data, error } = await supabase
            .from("chat_conversations")
            .insert({
              user_id: user.id,
              title,
              model,
              messages: payload as unknown as never,
              chat_type: chatType ?? "open",
            })
            .select("id")
            .single();
          if (error) throw error;
          ourLastTitleRef.current = title;
          setConversationId(data.id);
        } else {
          // Read current title; only refresh it if it's still the one
          // we generated last time — i.e. the user hasn't renamed in
          // the Save dialog.
          const { data: existing } = await supabase
            .from("chat_conversations")
            .select("title")
            .eq("id", conversationId)
            .maybeSingle();
          const safeToRetitle =
            !existing?.title || existing.title === ourLastTitleRef.current;
          if (safeToRetitle) {
            ourLastTitleRef.current = title;
            await supabase
              .from("chat_conversations")
              .update({
                messages: payload as unknown as never,
                model,
                title,
                chat_type: chatType ?? "open",
              })
              .eq("id", conversationId);
          } else {
            await supabase
              .from("chat_conversations")
              .update({
                messages: payload as unknown as never,
                model,
                chat_type: chatType ?? "open",
              })
              .eq("id", conversationId);
          }
        }
      } catch {
        // Swallow — auto-save must never interrupt the chat. If the
        // user shares/saves manually they'll see real errors there.
      } finally {
        inFlightRef.current = false;
      }
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [turns, model, enabled, user?.id, conversationId, seedSource, seedTitle, chatType]);
}